import type { FC } from 'react';
import React, { useMemo } from 'react';
import cn from 'classnames';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import type { TColors } from '@alfa-bank/site-button-v2/src/models';
import { NamedContainer } from '@alfa-bank/site-named-container';

import type { TApplicationsButton } from './models';

export const ApplicationsButton: FC<TApplicationsButton> = props => {
    const {
        BannerLink,
        Button,
        DataLayer,
        IconBag,
        IconPointerDown,
        Tooltip,
        analytics,
        applicationsButton: { items, link, title },
        styles,
        theme,
    } = props;

    const isItemsExists = Array.isArray(items) && items.length > 0;

    const buttonColors: TColors = theme === 'light' ? 'default' : 'inverted';

    const iconPointerDownClassNames = useMemo(() => {
        let iconPointerDown = cn(styles.internetBankIcon, styles.external);

        if (theme) {
            iconPointerDown = cn([styles[`${theme}InternetBankIcon`]], iconPointerDown);
        }

        return iconPointerDown;
    }, [theme, styles]);

    const buttonRender = useMemo(
        () => (
            <Button
                colors={buttonColors}
                href={isItemsExists ? undefined : link}
                size="xxs"
                type="button"
                view="tertiary"
            >
                <NamedContainer name="leftAddons">
                    <IconBag height={16} theme={theme} width={16} />
                </NamedContainer>
                {title}
                {isItemsExists && (
                    <NamedContainer name="rightAddons">
                        <IconPointerDown
                            className={iconPointerDownClassNames}
                            height={6}
                            width={10}
                        />
                    </NamedContainer>
                )}
            </Button>
        ),
        [
            Button,
            IconBag,
            IconPointerDown,
            isItemsExists,
            buttonColors,
            iconPointerDownClassNames,
            theme,
            title,
            link,
        ]
    );

    if (isItemsExists) {
        const tooltipContent = items.map(
            ({ analytics: analtcs, link: lnk, title: ttl, type }, rowIndex) => {
                let className = styles.dropDownText;

                if (type === 'header') {
                    className = styles.dropDownHeader;
                }

                return (
                    <DataLayer
                        // eslint-disable-next-line react/no-array-index-key
                        key={rowIndex}
                        data={analtcs}
                    >
                        <BannerLink link={lnk} title={ttl}>
                            <div className={className}>{ttl}</div>
                        </BannerLink>
                    </DataLayer>
                );
            }
        );

        return (
            <Tooltip
                className={cn(styles.internetBankTooltip, styles.external)}
                content={tooltipContent}
                contentContainerProps={{ showCloseButton: false }}
                onOpen={() => {
                    if (analytics) {
                        sendEvent(analytics);
                    }
                }}
            >
                {buttonRender}
            </Tooltip>
        );
    } else {
        return <DataLayer data={analytics}>{buttonRender}</DataLayer>;
    }
};
